import { Badge } from "@dl/web-core-js/ui";
import type { columns as defineColumns } from "@dl/web-core-js/table";
import { imageUrl } from "../../api/product";
import type { LazyProductPricingDTO } from "../../api/pricing";
import { ProductLink } from "../../components/ProductLink";

type Factory = ReturnType<typeof defineColumns<LazyProductPricingDTO>>;

/** 12-week unit sales — the volume figure all pricing screens lead with. */
export function twelveWeekUnits(product: LazyProductPricingDTO): number {
  const sales = product.kpi_sales;
  if (!sales) return 0;
  return (sales.twelve_weeks_units_cad ?? 0) + (sales.twelve_weeks_units_usd ?? 0);
}

/**
 * The identity + cost columns every pricing screen starts with, so the three
 * grids stay in step instead of drifting on shared columns.
 */
export function basePricingColumns(c: Factory) {
  return [
    c.custom(
      "image",
      () => "",
      (row) => {
        const url = imageUrl(row.image);
        return url ? (
          <img src={url} alt="" loading="lazy" className="size-12 rounded object-cover" />
        ) : (
          <span className="block size-12 rounded bg-surface-sunk" />
        );
      },
      { headerKey: "column.image", filter: "none", enableSorting: false, size: 64 },
    ),
    c.custom(
      "sku",
      (row) => row.metadata?.sku ?? "",
      (row) => <ProductLink id={row.id}>{row.metadata?.sku ?? ""}</ProductLink>,
      { headerKey: "column.sku", priority: 1 },
    ),
    c.text("name", (row) => row.description_en?.product_name ?? "", {
      headerKey: "column.name",
      priority: 2,
    }),
    c.text("type", (row) => row.type, { headerKey: "column.type", filter: "select", defaultVisible: false }),
    c.custom(
      "status",
      (row) => row.status,
      (row) => (
        <Badge variant={row.status === "AVAILABLE" ? "success" : "warning"} size="sm">
          {row.status}
        </Badge>
      ),
      { headerKey: "column.status", filter: "select", priority: 3 },
    ),
    c.custom(
      "sales_12w",
      (row) => twelveWeekUnits(row),
      (row) => (
        <span className="tabular">
          {twelveWeekUnits(row)}
          <span className="ml-1 text-[11px] text-ink-faint">
            {Math.round(row.kpi_sales?.twelve_weeks_sales_cad ?? 0)} CAD ·{" "}
            {Math.round(row.kpi_sales?.twelve_weeks_sales_usd ?? 0)} USD
          </span>
        </span>
      ),
      { headerKey: "pricing.sales-12w", priority: 3 },
    ),
    c.number("royalty", (row) => row.designer?.royalty ?? null, { headerKey: "config.royalty" }),
    c.number("cogs", (row) => round2(row.pricing?.cogs), { headerKey: "column.cogs" }),
  ];
}

export function round2(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value * 100) / 100;
}
